/**
 * Settings → Invoicing (design L): Branding, Numbering, Tax, Payment terms,
 * Payment options and Message templates. Reminders and messaging arrive with
 * the phase that sends messages.
 *
 * One form holds every tab so switching tabs keeps unsaved edits, but each
 * tab saves on its own with only its keys (settingsModel.buildPatch) — the
 * server merges at the top-level key, so tabs never overwrite each other.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import InvoiceRepository, { InvoiceSettings, PaymentTerms } from '../../../internals/repository/InvoiceRepository';
import { InvoiceApiError } from '../../../internals/repository/authedPosApi';
import InvoicePreview from '../components/InvoicePreview';
import TemplateEditor, { type TemplateSlot } from '../components/TemplateEditor';
import { useInvoiceSettings, useSelectedShop } from '../useInvoicing';
import { addDays, date, TERMS, todayColombo } from '../format';
import {
  ACCENTS, SERIES, TABS, TAX_PRESET, TOKENS,
  accentCheck, buildPatch, expandExample, firstProblemSlot, formatProblem, isDirty, mergeAfterSave, sampleDoc,
  serverFieldMessage, toForm, validateTab,
  type BankRow, type SeriesKey, type SettingsForm, type SettingsTab, type TemplateDefaults,
} from '../settingsModel';

const S3_BASE = 'https://netlise-image-bucket.s3.ap-southeast-1.amazonaws.com/public/';
const cardCls = 'bg-surface border border-borderc rounded-card';
const titleCls = 'text-[16px] font-semibold text-text1';
const hintCls = 'text-[12px] text-text3';

const initials = (name?: string) => (name || 'N').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

function Switch({ on, onClick, label, disabled }: { on: boolean; onClick: () => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`w-9 h-5 rounded-full p-0.5 flex-none transition-colors focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--brandSoft)] disabled:opacity-50 disabled:cursor-not-allowed ${on ? 'bg-brand' : 'bg-surface3'}`}
    >
      <span className={`block w-4 h-4 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : ''}`} style={{ boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
    </button>
  );
}

function Field({ label, error, hint, children, className = '' }: { label: React.ReactNode; error?: string; hint?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <label className={`flex flex-col min-w-0 ${className}`}>
      <span className="nl-label">{label}</span>
      {children}
      {error ? <span role="alert" className="text-[12px] text-neg mt-1">{error}</span> : hint ? <span className={`${hintCls} mt-1`}>{hint}</span> : null}
    </label>
  );
}

const errStyle = (e?: string): React.CSSProperties | undefined => (e ? { borderColor: 'var(--neg)' } : undefined);

export default function InvoiceSettingsView() {
  const shop = useSelectedShop();
  const shopId: string | undefined = shop?.shopId;
  const { settings, error, reload, replace } = useInvoiceSettings(shopId);
  const today = todayColombo();

  const [tab, setTab] = useState<SettingsTab>('branding');
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [tried, setTried] = useState<Partial<Record<SettingsTab, boolean>>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [customHex, setCustomHex] = useState('');
  const formatRefs = useRef<Partial<Record<SeriesKey, HTMLInputElement | null>>>({});
  const [activeSeries, setActiveSeries] = useState<SeriesKey>('INVOICE');
  const [tplSlot, setTplSlot] = useState<TemplateSlot>({ key: 'invoice_issued', ch: 'WHATSAPP', lang: 'en' });
  const loadedFor = useRef<string | undefined>();

  // Seed the form once per shop; later settings updates come from our own saves.
  useEffect(() => {
    if (settings && loadedFor.current !== shopId) {
      loadedFor.current = shopId;
      setForm(toForm(settings));
      setCustomHex(settings.branding?.accentColor ?? '');
      setTouched(new Set());
      setTried({});
      setServerErrors({});
    }
  }, [settings, shopId]);

  useEffect(() => {
    if (banner?.kind !== 'ok') return undefined;
    const t = window.setTimeout(() => setBanner(null), 3000);
    return () => window.clearTimeout(t);
  }, [banner]);

  const problems = useMemo(() => (form && settings ? validateTab(tab, form, settings) : {}), [tab, form, settings]);
  const dirty = form && settings ? isDirty(tab, form, settings) : false;

  const update = (patch: Partial<SettingsForm>) => {
    setForm((f) => (f ? { ...f, ...patch } : f));
    setServerErrors({});
    if (banner?.kind === 'err') setBanner(null);
  };
  const touch = (key: string) => setTouched((t) => (t.has(key) ? t : new Set(t).add(key)));
  const err = (key: string) => serverErrors[key] ?? (tried[tab] || touched.has(key) ? problems[key] : undefined);

  const save = async () => {
    if (!form || !settings || !shopId) return;
    setTried((t) => ({ ...t, [tab]: true }));
    if (Object.keys(problems).length) {
      const slot = tab === 'templates' ? firstProblemSlot(problems) : null;
      if (slot) setTplSlot(slot);
      setBanner({ kind: 'err', text: 'Fix the highlighted fields, then save again.' });
      return;
    }
    setSaving(true);
    setBanner(null);
    try {
      const { settings: next } = await InvoiceRepository.saveSettings(shopId, buildPatch(tab, form, settings));
      replace(next);
      setForm(mergeAfterSave(tab, form, next));
      setServerErrors({});
      setTried((t) => ({ ...t, [tab]: false }));
      setBanner({ kind: 'ok', text: 'Saved' });
    } catch (e: any) {
      if (e instanceof InvoiceApiError) {
        if (e.code === 'forbidden_role' || e.status === 403) {
          setBanner({ kind: 'err', text: 'Only the owner or an admin can change invoicing settings.' });
        } else {
          const fields: Record<string, string> = {};
          Object.entries(e.fields || {}).forEach(([path, code]) => { fields[path] = serverFieldMessage(path, String(code)); });
          setServerErrors(fields);
          const slot = tab === 'templates' ? firstProblemSlot(fields) : null;
          if (slot) setTplSlot(slot);
          setBanner({ kind: 'err', text: Object.keys(fields).length ? 'Some values were not accepted. Check the highlighted fields.' : e.message });
        }
      } else {
        setBanner({ kind: 'err', text: 'Could not save. Check your connection and try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (!form || !settings) return;
    setForm(mergeAfterSave(tab, form, settings));
    if (tab === 'branding') setCustomHex(settings.branding?.accentColor ?? '');
    setServerErrors({});
    setTried((t) => ({ ...t, [tab]: false }));
    setBanner(null);
  };

  /* ── Page shell ───────────────────────────────────────────────────── */

  const header = (
    <div className="flex flex-col gap-1 pb-5">
      <h1 className="text-[28px] font-semibold text-text1 tracking-[-0.015em] leading-tight m-0">Invoicing settings</h1>
      <p className="text-[14px] text-text2 m-0">Applies to invoices, quotations, receipts and the messages that carry them.</p>
    </div>
  );

  if (!shopId) {
    return (
      <div className="h-screen flex flex-col w-full p-6 overflow-auto [&>*]:shrink-0">
        {header}
        <div className={`${cardCls} p-6 text-text2`}>Select a shop to see its invoicing settings.</div>
      </div>
    );
  }

  const tabFooter = (
    <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-borderc">
      {dirty && <span className={`${hintCls} mr-auto`}>Unsaved changes</span>}
      <button type="button" className="nl-btn nl-btn--ghost" disabled={!dirty || saving} onClick={discard}>Discard</button>
      <button type="button" className="nl-btn nl-btn--primary" disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
    </div>
  );

  return (
    <div className="h-screen flex flex-col w-full p-6 overflow-auto [&>*]:shrink-0">
      {header}

      {banner && (
        <div
          role={banner.kind === 'err' ? 'alert' : 'status'}
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm border ${banner.kind === 'ok' ? 'bg-pos-soft border-borderc' : 'bg-neg-soft border-borderc'}`}
          style={{ color: banner.kind === 'ok' ? 'var(--posFg)' : 'var(--negFg)' }}
        >
          {banner.text}
        </div>
      )}

      <div className="grid grid-cols-1 min-[1100px]:grid-cols-[220px_minmax(0,1fr)] gap-5 items-start max-w-[1360px]">
        <nav aria-label="Invoicing settings" className={`${cardCls} p-2 flex flex-row min-[1100px]:flex-col gap-0.5 overflow-x-auto min-[1100px]:sticky min-[1100px]:top-0`}>
          {TABS.map((t) => {
            const on = t.key === tab;
            const unsaved = form && settings && t.key !== tab && isDirty(t.key, form, settings);
            return (
              <button
                key={t.key}
                type="button"
                aria-current={on ? 'page' : undefined}
                onClick={() => { setTab(t.key); setBanner(null); setServerErrors({}); }}
                className={`flex items-center gap-2 text-left h-9 px-3 rounded-lg text-[14px] whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--brandSoft)] ${on ? 'bg-brand-soft text-brand font-semibold' : 'text-text2 font-medium hover:bg-surface2'}`}
              >
                {t.label}
                {unsaved && <span className="w-1.5 h-1.5 rounded-full bg-brand" title="Unsaved changes" aria-label="Unsaved changes" />}
              </button>
            );
          })}
        </nav>

        <div className="flex flex-col gap-4 min-w-0">
          {error && !settings && (
            <div role="alert" className={`${cardCls} p-6 flex flex-col gap-3 items-start`}>
              <span className="text-text1 font-semibold">Could not load invoicing settings</span>
              <span className="text-text2 text-sm">{error.message}</span>
              <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm" onClick={reload}>Try again</button>
            </div>
          )}
          {!error && (!settings || !form) && (
            <div className={`${cardCls} p-6 flex flex-col gap-4`} aria-busy="true" aria-label="Loading">
              {[40, 70, 55, 80].map((w) => <div key={w} className="h-10 rounded-lg bg-surface2 animate-pulse" style={{ width: `${w}%` }} />)}
            </div>
          )}
          {settings && form && tab === 'branding' && (
            <BrandingTab form={form} update={update} err={err} touch={touch} shop={shop} today={today} customHex={customHex} setCustomHex={setCustomHex} footer={tabFooter} />
          )}
          {settings && form && tab === 'numbering' && (
            <NumberingTab
              form={form} settings={settings} update={update} err={err} touch={touch} today={today} footer={tabFooter}
              formatRefs={formatRefs} activeSeries={activeSeries} setActiveSeries={setActiveSeries}
            />
          )}
          {settings && form && tab === 'tax' && <TaxTab form={form} update={update} err={err} touch={touch} footer={tabFooter} />}
          {settings && form && tab === 'terms' && <TermsTab form={form} update={update} err={err} touch={touch} today={today} footer={tabFooter} />}
          {settings && form && tab === 'payments' && <PaymentsTab form={form} settings={settings} update={update} err={err} touch={touch} shop={shop} footer={tabFooter} />}
          {settings && form && tab === 'templates' && (
            <TemplateEditor
              templates={form.templates}
              defaults={(settings as InvoiceSettings & { templateDefaults?: TemplateDefaults }).templateDefaults}
              slot={tplSlot}
              setSlot={setTplSlot}
              onChange={(templates) => update({ templates })}
              err={err}
              touch={touch}
              shopName={shop?.shopName}
              shopPhone={shop?.phoneNumber}
              footer={tabFooter}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface TabProps {
  form: SettingsForm;
  update: (p: Partial<SettingsForm>) => void;
  err: (key: string) => string | undefined;
  touch: (key: string) => void;
  footer: React.ReactNode;
}

/* ── Branding ───────────────────────────────────────────────────────── */

function BrandingTab({ form, update, err, touch, shop, today, customHex, setCustomHex, footer }: TabProps & { shop: any; today: string; customHex: string; setCustomHex: (s: string) => void }) {
  const check = accentCheck(form.accentColor);
  const doc = useMemo(() => sampleDoc(form, today, shop), [form, today, shop]);
  const logo = shop?.image ? `${S3_BASE}${shop.image}` : null;
  const pickHex = (v: string) => {
    const hex = v.startsWith('#') ? v : `#${v}`;
    setCustomHex(v);
    update({ accentColor: /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex.toUpperCase() : v });
  };

  return (
    <div className="grid grid-cols-1 min-[1100px]:grid-cols-[minmax(0,1fr)_400px] gap-4 items-start">
      <div className={cardCls}>
        <div className="p-5 flex flex-col gap-5">
          <span className={titleCls}>Branding</span>

          <div className="flex gap-3.5 items-center">
            <div className="w-[72px] h-[72px] rounded-[14px] border border-dashed border-borderc2 bg-surface2 grid place-items-center overflow-hidden flex-none">
              {logo
                ? <img src={logo} alt={`${shop?.shopName ?? 'Shop'} logo`} className="w-full h-full object-cover" />
                : <span className="text-[22px] font-bold text-text2">{initials(shop?.shopName)}</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold text-text1">Logo</span>
              <span className="text-[12px] text-text2">Invoices use your shop logo from Company information.</span>
              <span className={hintCls}>Uploading a separate invoice logo comes in a later release.</span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="nl-label !mb-0">Accent colour</span>
            <div className="flex gap-2 flex-wrap items-center">
              {ACCENTS.map((c) => {
                const on = form.accentColor.toUpperCase() === c;
                return (
                  <button
                    key={c}
                    type="button"
                    aria-label={c}
                    aria-pressed={on}
                    onClick={() => { setCustomHex(c); update({ accentColor: c }); }}
                    className="w-[34px] h-[34px] rounded-[10px] border-2 focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--brandSoft)]"
                    style={{ background: c, borderColor: on ? 'var(--text)' : 'transparent', boxShadow: 'inset 0 0 0 2px var(--surface)' }}
                  />
                );
              })}
              <label className="flex items-center gap-2 ml-1">
                <span className="sr-only">Custom colour</span>
                <span className="w-[34px] h-[34px] rounded-[10px] border border-borderc2 flex-none" style={{ background: check.valid ? form.accentColor : 'transparent' }} aria-hidden />
                <input
                  className="nl-input nl-input--sm font-mono !w-[110px] uppercase"
                  value={customHex}
                  maxLength={7}
                  placeholder="#4E45E4"
                  aria-invalid={!!err('branding.accentColor')}
                  onBlur={() => touch('branding.accentColor')}
                  onChange={(e) => pickHex(e.target.value.trim())}
                  style={errStyle(err('branding.accentColor'))}
                />
              </label>
            </div>
            <span className="text-[12px] font-medium" style={{ color: !check.valid ? 'var(--negFg)' : check.passes ? 'var(--posFg)' : 'var(--warnFg)' }}>
              {err('branding.accentColor') ?? check.message}
            </span>
          </div>

          <Field label="Footer text" error={err('branding.footerText')} hint="Shown at the bottom of every invoice, quotation and receipt.">
            <textarea className="nl-input" rows={2} value={form.footerText} onChange={(e) => update({ footerText: e.target.value })} onBlur={() => touch('branding.footerText')} maxLength={240} />
          </Field>
        </div>
        {footer}
      </div>

      <div className="rounded-card border border-borderc p-[18px] flex flex-col items-center gap-2.5 overflow-x-auto" style={{ background: 'var(--paperCanvas)' }}>
        <span className="self-start text-[11px] font-semibold tracking-[.08em] text-text3">PREVIEW</span>
        <InvoicePreview
          doc={doc}
          shop={shop}
          scale={0.6}
          branding={{ accentColor: check.valid ? form.accentColor : undefined, footerText: form.footerText, bankAccounts: form.bankTransfer ? form.bankAccounts : [], shopTin: form.shopTin }}
        />
      </div>
    </div>
  );
}

/* ── Numbering ──────────────────────────────────────────────────────── */

function NumberingTab({
  form, settings, update, err, touch, today, footer, formatRefs, activeSeries, setActiveSeries,
}: TabProps & {
  settings: InvoiceSettings; today: string;
  formatRefs: React.MutableRefObject<Partial<Record<SeriesKey, HTMLInputElement | null>>>;
  activeSeries: SeriesKey; setActiveSeries: (k: SeriesKey) => void;
}) {
  const setSeries = (k: SeriesKey, patch: Partial<SettingsForm['series'][SeriesKey]>) =>
    update({ series: { ...form.series, [k]: { ...form.series[k], ...patch } } });

  /** Token chips insert at the caret of the last focused format box. */
  const insertToken = (token: string) => {
    const k = activeSeries;
    const el = formatRefs.current[k];
    const value = form.series[k].format;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    setSeries(k, { format: value.slice(0, start) + token + value.slice(end) });
    window.requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const usesBranch = (f: string) => /\{(BR|BRANCH)\}/.test(f);

  const row = (k: SeriesKey, label: string) => {
    const s = form.series[k];
    const serverNext = settings.series?.[k]?.nextNumber;
    const fmtErr = err(`series.${k}.format`);
    const ok = !formatProblem(s.format.trim(), form.branchCode || undefined);
    const example = ok ? expandExample(s.format.trim(), today, form.branchCode, s.next || serverNext || 1) : '—';
    return (
      <div key={k} className="grid grid-cols-1 sm:grid-cols-2 min-[1280px]:grid-cols-3 gap-3.5 p-3.5 rounded-xl bg-surface2">
        <Field label={`${label} format`} error={fmtErr} hint={usesBranch(s.format) && !form.branchCode ? 'Set the branch code on the Tax tab.' : undefined}>
          <input
            ref={(el) => { formatRefs.current[k] = el; }}
            className="nl-input font-mono"
            value={s.format}
            spellCheck={false}
            aria-invalid={!!fmtErr}
            style={errStyle(fmtErr)}
            onFocus={() => setActiveSeries(k)}
            onBlur={() => touch(`series.${k}.format`)}
            onChange={(e) => { setSeries(k, { format: e.target.value }); touch(`series.${k}.format`); }}
          />
        </Field>
        <Field
          label="Next number"
          error={err(`series.${k}.nextNumber`)}
          hint={serverNext ? `Now ${serverNext}. You can raise it, not lower it.` : 'Starts at 1.'}
        >
          <input
            className="nl-input font-mono"
            inputMode="numeric"
            value={s.next}
            placeholder={serverNext ? String(serverNext) : '1'}
            aria-invalid={!!err(`series.${k}.nextNumber`)}
            style={errStyle(err(`series.${k}.nextNumber`))}
            onBlur={() => touch(`series.${k}.nextNumber`)}
            onChange={(e) => { setSeries(k, { next: e.target.value.replace(/[^\d]/g, '') }); touch(`series.${k}.nextNumber`); }}
          />
        </Field>
        <div className="flex flex-col min-w-0">
          <span className="nl-label">Next one will be</span>
          <span className="h-10 flex items-center px-3 rounded-[10px] bg-brand-soft text-brand font-mono font-semibold truncate" aria-live="polite">{example}</span>
        </div>
      </div>
    );
  };

  const tax = form.series.TAX_INVOICE;
  const savedTax = !!settings.series?.TAX_INVOICE?.format;

  return (
    <div className={cardCls}>
      <div className="p-5 flex flex-col gap-4">
        <span className={titleCls}>Numbering</span>
        {SERIES.map((s) => row(s.key, s.label))}

        <div className="flex gap-1.5 flex-wrap items-center">
          <span className="text-[13px] text-text2 mr-1">Tokens</span>
          {TOKENS.map((t) => (
            <button
              key={t.token}
              type="button"
              title={t.desc}
              aria-label={`Insert ${t.token}: ${t.desc}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertToken(t.token)}
              className="h-7 px-2.5 rounded-[7px] border border-borderc2 bg-surface text-text1 font-mono text-[12px] hover:bg-surface2 focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--brandSoft)]"
            >
              {t.token}
            </button>
          ))}
        </div>
        <span className={hintCls}>Numbers are given when an invoice is issued, never to drafts, so the series has no gaps.</span>

        <div className="flex flex-col gap-3 pt-4 border-t border-borderc">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <span className="text-[14px] font-semibold text-text1">Tax invoice series <span className="font-normal text-text3">· optional</span></span>
              <span className="text-[12px] text-text2">A separate series for tax invoices, for example 26OCT_NUG_00001.</span>
            </div>
            <button type="button" className="nl-btn nl-btn--soft nl-btn--sm" onClick={() => { update({ series: { ...form.series, TAX_INVOICE: { ...tax, format: TAX_PRESET } } }); setActiveSeries('TAX_INVOICE'); }}>
              Use the tax preset
            </button>
          </div>
          {(tax.format || savedTax) ? row('TAX_INVOICE', 'Tax invoice') : <span className={hintCls}>Not set. Tax invoices use the Invoice series.</span>}
        </div>
      </div>
      {footer}
    </div>
  );
}

/* ── Tax ────────────────────────────────────────────────────────────── */

function TaxTab({ form, update, err, touch, footer }: TabProps) {
  const setRate = (i: number, patch: Partial<SettingsForm['taxRates'][number]>) =>
    update({ taxRates: form.taxRates.map((r, j) => (j === i ? { ...r, ...patch } : patch.isDefault ? { ...r, isDefault: false } : r)) });
  const remove = (i: number) => {
    const rest = form.taxRates.filter((_, j) => j !== i);
    if (form.taxRates[i].isDefault && rest.length) rest[0] = { ...rest[0], isDefault: true };
    update({ taxRates: rest });
  };
  const add = () => update({ taxRates: [...form.taxRates, { code: '', label: '', rate: '', isDefault: form.taxRates.length === 0 }] });

  return (
    <div className={cardCls}>
      <div className="p-5 flex flex-col gap-5">
        <span className={titleCls}>Tax</span>

        <div className="flex flex-col gap-2">
          <div className="border border-borderc rounded-xl overflow-x-auto">
            <table className="w-full min-w-[560px] text-[14px]">
              <thead>
                <tr className="bg-surface2 text-[11px] font-semibold tracking-[.06em] text-text3 text-left">
                  <th className="px-3.5 py-2 font-semibold w-[130px]">CODE</th>
                  <th className="px-3.5 py-2 font-semibold">LABEL</th>
                  <th className="px-3.5 py-2 font-semibold w-[110px] text-right">RATE %</th>
                  <th className="px-3.5 py-2 font-semibold w-[90px] text-center">DEFAULT</th>
                  <th className="w-[52px]"><span className="sr-only">Remove</span></th>
                </tr>
              </thead>
              <tbody>
                {form.taxRates.map((r, i) => (
                  <tr key={i} className="border-t border-borderc align-top">
                    <td className="px-3.5 py-2">
                      <input aria-label={`Rate ${i + 1} code`} className="nl-input nl-input--sm font-mono uppercase" value={r.code} maxLength={20}
                        style={errStyle(err(`taxRates[${i}].code`))} onBlur={() => touch(`taxRates[${i}].code`)}
                        onChange={(e) => setRate(i, { code: e.target.value.toUpperCase().replace(/\s/g, '') })} />
                      {err(`taxRates[${i}].code`) && <span className="block text-[12px] text-neg mt-1">{err(`taxRates[${i}].code`)}</span>}
                    </td>
                    <td className="px-3.5 py-2">
                      <input aria-label={`Rate ${i + 1} label`} className="nl-input nl-input--sm" value={r.label} maxLength={40}
                        style={errStyle(err(`taxRates[${i}].label`))} onBlur={() => touch(`taxRates[${i}].label`)}
                        onChange={(e) => setRate(i, { label: e.target.value })} />
                      {err(`taxRates[${i}].label`) && <span className="block text-[12px] text-neg mt-1">{err(`taxRates[${i}].label`)}</span>}
                    </td>
                    <td className="px-3.5 py-2">
                      <input aria-label={`Rate ${i + 1} percent`} className="nl-input nl-input--sm font-mono text-right" inputMode="decimal" value={r.rate}
                        style={errStyle(err(`taxRates[${i}].rateBp`))} onBlur={() => touch(`taxRates[${i}].rateBp`)}
                        onChange={(e) => setRate(i, { rate: e.target.value.replace(/[^\d.]/g, '') })} />
                      {err(`taxRates[${i}].rateBp`) && <span className="block text-[12px] text-neg mt-1">{err(`taxRates[${i}].rateBp`)}</span>}
                    </td>
                    <td className="px-3.5 py-2 text-center">
                      <input type="radio" name="defaultRate" className="nl-check mt-2" aria-label={`Make ${r.label || r.code || `rate ${i + 1}`} the default`} checked={r.isDefault} onChange={() => setRate(i, { isDefault: true })} />
                    </td>
                    <td className="px-2 py-2">
                      <button type="button" className="nl-btn nl-btn--ghost nl-btn--sm" aria-label={`Remove ${r.label || r.code || `rate ${i + 1}`}`} onClick={() => remove(i)}>✕</button>
                    </td>
                  </tr>
                ))}
                {!form.taxRates.length && (
                  <tr className="border-t border-borderc"><td colSpan={5} className="px-3.5 py-3 text-text3 text-[13px]">No tax rates. Lines will have no tax.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {err('taxRates') && <span role="alert" className="text-[12px] text-neg">{err('taxRates')}</span>}
          <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm self-start" onClick={add}>+ Add rate</button>
        </div>

        <div className="flex items-start justify-between gap-4 px-4 py-3.5 rounded-xl border border-borderc">
          <span>
            <span className="block text-[14px] font-semibold text-text1">Tax invoices</span>
            <span className="block text-[12px] text-text2 mt-0.5">Issue Sri Lankan tax invoices with your TIN, the buyer's TIN and the supply date.</span>
          </span>
          <Switch on={form.taxInvoiceEnabled} label="Tax invoices" onClick={() => update({ taxInvoiceEnabled: !form.taxInvoiceEnabled })} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 min-[1280px]:grid-cols-3 gap-4">
          <Field label="Tax invoice mode">
            <select className="nl-select" value={form.taxInvoiceMode} disabled={!form.taxInvoiceEnabled}
              onChange={(e) => update({ taxInvoiceMode: e.target.value as SettingsForm['taxInvoiceMode'] })}>
              <option value="ASK">Ask per invoice</option>
              <option value="ALWAYS_FOR_VAT_CUSTOMERS">Always for VAT-registered customers</option>
              <option value="NEVER">Never</option>
            </select>
          </Field>
          <Field label="Shop TIN" error={err('shopTin')} hint="9 digits, for example 114567890 or 114567890-7000.">
            <input className="nl-input font-mono" value={form.shopTin} inputMode="numeric" maxLength={14} aria-invalid={!!err('shopTin')} style={errStyle(err('shopTin'))}
              onBlur={() => touch('shopTin')} onChange={(e) => update({ shopTin: e.target.value.replace(/[^\d-]/g, '') })} />
          </Field>
          <Field label="VAT registration number" error={err('vatNumber')}>
            <input className="nl-input font-mono" value={form.vatNumber} maxLength={20} onBlur={() => touch('vatNumber')} onChange={(e) => update({ vatNumber: e.target.value })} />
          </Field>
          <Field label="Branch code" error={err('branchCode')} hint="Used by {BR} in number formats.">
            <input className="nl-input font-mono uppercase" value={form.branchCode} maxLength={10} aria-invalid={!!err('branchCode')} style={errStyle(err('branchCode'))}
              onBlur={() => touch('branchCode')} onChange={(e) => update({ branchCode: e.target.value.toUpperCase().replace(/\s/g, '') })} />
          </Field>
          <Field label="Rounding" hint="Check with your accountant (OQ-INV-1).">
            <select className="nl-select" value={form.roundWhole ? 'WHOLE' : 'CENT'} onChange={(e) => update({ roundWhole: e.target.value === 'WHOLE' })}>
              <option value="CENT">Nearest cent</option>
              <option value="WHOLE">Whole rupee</option>
            </select>
          </Field>
        </div>
      </div>
      {footer}
    </div>
  );
}

/* ── Payment terms ──────────────────────────────────────────────────── */

function TermsTab({ form, update, err, touch, today, footer }: TabProps & { today: string }) {
  const customDays = /^\d+$/.test(form.customDays) ? Number(form.customDays) : null;
  const quoteDays = /^\d+$/.test(form.quotationValidDays) ? Number(form.quotationValidDays) : null;
  return (
    <div className={cardCls}>
      <div className="p-5 flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <span className={titleCls}>Default payment terms</span>
          <span className="text-[13px] text-text2">Used for new invoices. You can change it per invoice.</span>
        </div>
        <div role="radiogroup" aria-label="Default payment terms" className="flex flex-col gap-2.5 max-w-[560px]">
          {TERMS.map((t) => {
            const on = form.terms === t.value;
            const days = t.days ?? customDays;
            return (
              <div key={t.value}
                className={`flex items-center gap-3 px-3.5 py-3 rounded-[10px] border-[1.5px] transition-colors ${on ? 'border-brand bg-brand-soft' : 'border-borderc bg-surface hover:bg-surface2'}`}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => update({ terms: t.value as PaymentTerms })}
                  className="flex items-center gap-3 flex-1 text-left text-[14px] font-medium text-text1 focus-visible:outline-none focus-visible:underline"
                >
                  <span className={`w-4 h-4 rounded-full border-2 grid place-items-center flex-none ${on ? 'border-brand' : 'border-borderc2'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-brand' : ''}`} />
                  </span>
                  <span className="flex-1">{t.value === 'CUSTOM' ? 'Custom' : t.label}</span>
                </button>
                {t.value === 'CUSTOM' && on && (
                  <label className="flex items-center gap-2">
                    <span className="sr-only">Custom days</span>
                    <input className="nl-input nl-input--sm font-mono !w-[72px] text-right" inputMode="numeric" value={form.customDays} autoFocus
                      aria-invalid={!!err('defaultCustomTermDays')} style={errStyle(err('defaultCustomTermDays'))}
                      onBlur={() => touch('defaultCustomTermDays')} onChange={(e) => update({ customDays: e.target.value.replace(/[^\d]/g, '') })} />
                    <span className="text-[13px] text-text2">days</span>
                  </label>
                )}
                <span className="text-[12px] text-text3 whitespace-nowrap">
                  {days != null && (t.value !== 'CUSTOM' || on) ? `Issued today, due ${date(addDays(today, days))}` : ''}
                </span>
              </div>
            );
          })}
          {err('defaultCustomTermDays') && <span role="alert" className="text-[12px] text-neg">{err('defaultCustomTermDays')}</span>}
        </div>

        <Field className="max-w-[260px]" label="Quotations are valid for" error={err('quotationValidDays')}
          hint={quoteDays ? `Sent today, valid until ${date(addDays(today, quoteDays))}.` : undefined}>
          <div className="flex items-center gap-2">
            <input className="nl-input font-mono !w-[96px] text-right" inputMode="numeric" value={form.quotationValidDays}
              aria-invalid={!!err('quotationValidDays')} style={errStyle(err('quotationValidDays'))}
              onBlur={() => touch('quotationValidDays')} onChange={(e) => update({ quotationValidDays: e.target.value.replace(/[^\d]/g, '') })} />
            <span className="text-[13px] text-text2">days</span>
          </div>
        </Field>

        <div className="grid grid-cols-1 min-[1100px]:grid-cols-2 gap-4">
          <Field label="Default notes" hint="Pre-filled on new invoices and quotations.">
            <textarea className="nl-input" rows={3} value={form.defaultNotes} maxLength={1000} onChange={(e) => update({ defaultNotes: e.target.value })} />
          </Field>
          <Field label="Default terms" hint="Payment instructions, warranty or cancellation terms.">
            <textarea className="nl-input" rows={3} value={form.defaultTerms} maxLength={2000} onChange={(e) => update({ defaultTerms: e.target.value })} />
          </Field>
        </div>
      </div>
      {footer}
    </div>
  );
}

/* ── Payment options ────────────────────────────────────────────────── */

function OptionRow({ label, desc, on, onToggle, disabled, children }: { label: string; desc: string; on: boolean; onToggle: () => void; disabled?: boolean; children?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2.5 px-5 py-3.5 border-t border-borderc">
      <div className="flex items-start gap-3">
        <Switch on={on} onClick={onToggle} label={label} disabled={disabled} />
        <span className="flex flex-col">
          <span className={`text-[14px] font-semibold ${disabled ? 'text-text3' : 'text-text1'}`}>{label}</span>
          <span className="text-[12px] text-text2">{desc}</span>
        </span>
      </div>
      {children && <div className="ml-12">{children}</div>}
    </div>
  );
}

function PaymentsTab({ form, settings, update, err, touch, shop, footer }: TabProps & { settings: InvoiceSettings; shop: any }) {
  const setAccount = (i: number, patch: Partial<BankRow>) =>
    update({ bankAccounts: form.bankAccounts.map((b, j) => (j === i ? { ...b, ...patch } : b)) });
  const addAccount = () => update({ bankAccounts: [...form.bankAccounts, { bankName: '', branch: '', accountName: shop?.shopName ?? '', accountNumber: '' }] });
  const toggleBank = () => {
    const on = !form.bankTransfer;
    update({ bankTransfer: on, ...(on && !form.bankAccounts.length ? { bankAccounts: [{ bankName: '', branch: '', accountName: shop?.shopName ?? '', accountNumber: '' }] } : {}) });
  };
  const hasQr = !!settings.lankaQrKey;

  const acctField = (i: number, key: keyof BankRow, label: string, mono = false, optional = false) => {
    const path = `bankAccounts[${i}].${key}`;
    return (
      <Field label={optional ? <>{label} <span className="text-text3 font-normal">· optional</span></> : label} error={err(path)}>
        <input className={`nl-input nl-input--sm ${mono ? 'font-mono' : ''}`} value={form.bankAccounts[i][key]} maxLength={60}
          aria-invalid={!!err(path)} style={errStyle(err(path))} onBlur={() => touch(path)}
          onChange={(e) => setAccount(i, { [key]: e.target.value } as Partial<BankRow>)} />
      </Field>
    );
  };

  return (
    <div className={`${cardCls} overflow-hidden`}>
      <div className="px-5 pt-[18px] pb-3 flex flex-col gap-0.5">
        <span className={titleCls}>Payment options shown to customers</span>
        <span className="text-[13px] text-text2">These appear on the invoice link and the PDF.</span>
      </div>

      <OptionRow label="Bank transfer" desc="Account details with copy buttons and the invoice number as reference." on={form.bankTransfer} onToggle={toggleBank}>
        {(form.bankTransfer || form.bankAccounts.length > 0) && (
          <div className="flex flex-col gap-3">
            {form.bankAccounts.map((b, i) => (
              <div key={i} className="rounded-[10px] bg-surface2 p-3.5 grid grid-cols-1 sm:grid-cols-2 min-[1280px]:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-start">
                {acctField(i, 'bankName', 'Bank')}
                {acctField(i, 'branch', 'Branch', false, true)}
                {acctField(i, 'accountName', 'Account name')}
                {acctField(i, 'accountNumber', 'Account number', true)}
                <button type="button" className="nl-btn nl-btn--ghost nl-btn--sm min-[1280px]:mt-[22px] justify-self-start" aria-label={`Remove account ${i + 1}`} onClick={() => update({ bankAccounts: form.bankAccounts.filter((_, j) => j !== i) })}>
                  Remove
                </button>
              </div>
            ))}
            {err('bankAccounts') && <span role="alert" className="text-[12px] text-neg">{err('bankAccounts')}</span>}
            <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm self-start" onClick={addAccount}>+ Add account</button>
          </div>
        )}
      </OptionRow>

      <OptionRow
        label="LankaQR"
        desc="Customers scan your LankaQR code or save the image."
        on={form.lankaQr}
        disabled={!hasQr && !form.lankaQr}
        onToggle={() => update({ lankaQr: !form.lankaQr })}
      >
        <div className="flex gap-3.5 items-center p-3 rounded-[10px] bg-surface2">
          <span className="w-14 h-14 rounded-lg border border-dashed border-borderc2 grid place-items-center font-mono text-[9px] text-text3 flex-none"
            style={{ background: 'repeating-linear-gradient(135deg, var(--surface) 0 5px, var(--surface2) 5px 10px)' }} aria-hidden>QR</span>
          <span className="flex flex-col gap-1">
            <span className="text-[13px] text-text2">{hasQr ? 'Your LankaQR image is on file.' : 'Upload your LankaQR image from your bank app.'}</span>
            <span className={hintCls}>Uploading the image comes in a later release, so LankaQR stays off until then.</span>
          </span>
          <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm ml-auto" disabled>Upload image</button>
        </div>
      </OptionRow>

      <OptionRow label="Card" desc="Needs a payment provider. Connect one to turn this on." on={form.card} disabled={!form.card} onToggle={() => update({ card: !form.card })} />

      <OptionRow label="Pay at the shop" desc="Shows your address and opening hours." on={form.payAtShop} onToggle={() => update({ payAtShop: !form.payAtShop })}>
        {form.payAtShop && shop?.address && <div className="px-3.5 py-3 rounded-[10px] bg-surface2 text-[13px] text-text2">{shop.address}</div>}
      </OptionRow>

      <OptionRow label="Require a slip for bank transfers" desc="Customers must upload a slip or screenshot with “I’ve paid”." on={form.requireProof} onToggle={() => update({ requireProof: !form.requireProof })} />

      {footer}
    </div>
  );
}
